import { UploadOutlined } from '@ant-design/icons';
import { useTranslate } from '@tolgee/react';
import { useMemoizedFn, useRequest, useToggle } from 'ahooks';
import { Button, Form, Input, message, Modal, Select, Upload } from 'antd';
import { RcFile, UploadChangeParam, UploadFile } from 'antd/es/upload/interface';
import { useAuthorization } from 'components/Authorization';
import StatusButton from 'components/StatusButton';
import { useCustomUpload } from 'hooks/business/useUploadResource';
import { useMemo, useState } from 'react';
import { returnReviewReject } from 'services/api/return';
import { platformUploadSizeLimitMap } from 'services/config/platform';
import { ReturnActionMercadoProps } from './types';

export const ReturnReviewReject = (props: ReturnActionMercadoProps) => {
  const { returnSn, internalStoreId } = props;
  const { t } = useTranslate();
  const [visible, { toggle: toggleVisible }] = useToggle();
  const [isUploadRequired, setIsUploadRequired] = useState(false);
  const customUpload = useCustomUpload();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const { token } = useAuthorization();

  const [form] = Form.useForm();

  const uploadAccept = 'jpg,jpeg,bmp,pdf,png';
  const reasonOptions = useMemo(
    () => [
      {
        label: t('产品到达时已破损'),
        value: 'SRF2',
      },
      {
        label: t('退货不完整'),
        value: 'SRF3',
      },
      {
        label: t('退回的产品与我发送的产品不同'),
        value: 'SRF4',
      },
      {
        label: t('产品不在包裹中'),
        value: 'SRF5',
      },
      {
        label: t('产品有其他缺陷'),
        value: 'SRF6',
      },
      {
        label: t('包裹尚未送达'),
        value: 'SRF7',
      },
    ],
    [t],
  );

  const { loading, runAsync: submitForm } = useRequest(
    (values) =>
      returnReviewReject({
        platform: 'MERCADO',
        returnSn,
        reasonId: values.reasonId,
        comment: values.comment,
        image: values.image,
      }),
    {
      manual: true,
      onSuccess() {
        toggleVisible();
        message.success(t('提交成功'));
      },
      onError() {
        message.error(t('translation.操作失败，请稍后再试'));
      },
    },
  );

  const handleChange = (info: UploadChangeParam<any>) => {
    const { fileList } = info;
    setFileList(fileList);
    const fList: UploadFile[] = [];
    fileList.forEach((v) => {
      if (v.error) {
        message.error(`${t('trade.上传失败')}: ${v.name}`);
      }
      if (v.error || !v.status) {
        return;
      }
      let { url } = v;
      if (v.response?.code === 200) {
        url = v.response?.data?.url;
      }
      fList.push({
        uid: v.uid,
        name: v.name,
        status: v.status,
        url,
      });
    });
    if (fList.length !== fileList.length) {
      setFileList(fList);
    }
  };

  const handleBeforeUpload = useMemoizedFn((file: RcFile) => {
    const uploadSizeLimit = platformUploadSizeLimitMap['mercado'];

    if (uploadAccept.split(',').every((value) => !file.type.includes(value))) {
      message.warning(
        t('支持文件格式提示', {
          type: uploadAccept,
        }),
      );
      return Upload.LIST_IGNORE;
    }
    if (file.size > uploadSizeLimit) {
      message.warn(
        t('上传文件大小限制', {
          size: `${(uploadSizeLimit / 1024 / 1024).toFixed(2)}M`,
        }),
      );
      return Upload.LIST_IGNORE;
    }
  });

  return (
    <>
      <StatusButton
        size="small"
        onClick={async () => {
          toggleVisible();
          return Promise.resolve();
        }}
      >
        {t('验收退货不通过')}
      </StatusButton>
      <Modal
        visible={visible}
        title={t('验收退货不通过')}
        okText={t('feedback.提交')}
        onOk={() => {
          form.submit();
        }}
        okButtonProps={{ loading }}
        onCancel={toggleVisible}
      >
        <Form
          form={form}
          layout="vertical"
          onValuesChange={(_, values) => {
            // 上传证据必填
            setIsUploadRequired(['SRF2', 'SRF4'].includes(values.reasonId));
          }}
          onFinish={(values) => {
            const image = fileList.map((v) => v?.response?.data?.url);
            submitForm({
              ...values,
              image,
            });
          }}
        >
          <Form.Item
            label={t('trade.选择原因')}
            name="reasonId"
            required
            rules={[
              {
                required: true,
                message: t('trade.请选择原因'),
                whitespace: true,
              },
            ]}
          >
            <Select placeholder={t('trade.请选择原因')} options={reasonOptions} />
          </Form.Item>
          <Form.Item
            label={t('feedback.描述')}
            name="comment"
            required
            rules={[
              {
                type: 'string',
                required: true,
                message: t('trade.请输入描述'),
                whitespace: true,
              },
            ]}
          >
            <Input.TextArea
              placeholder={t('trade.请输入描述')}
              showCount
              maxLength={320}
              autoSize={{ minRows: 4, maxRows: 6 }}
            />
          </Form.Item>
          <Form.Item
            label={t('trade.上传证据')}
            name="image"
            required={isUploadRequired}
            rules={[
              {
                required: isUploadRequired,
                validator: (_, value) => {
                  if (!isUploadRequired) return Promise.resolve();

                  if (!value || value?.length === 0) {
                    return Promise.reject(t('phrase.请上传文件'));
                  }
                  return Promise.resolve();
                },
                validateTrigger: ['onSubmit'],
              },
            ]}
          >
            <Upload
              multiple
              headers={{
                [import.meta.env.VITE_AUTH_HEADER as string]: `Bearer ${token}`,
              }}
              accept={uploadAccept}
              beforeUpload={handleBeforeUpload}
              fileList={fileList}
              onChange={handleChange}
              customRequest={(options) =>
                customUpload(options, {
                  storeId: internalStoreId,
                  fileType: 'IMAGE',
                  reqSource: 'ATTACHMENT',
                })
              }
            >
              <Button icon={<UploadOutlined />}>{t('trade.上传证据')}</Button>
              <p className="text-content-200 !mt-2">
                {t('upload.fileLimit', {
                  size: 25,
                  type: uploadAccept,
                })}
              </p>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
